/**
 * Defers building a module-level schema until it is first used.
 *
 * Why: a Cloudflare Worker must evaluate its global scope within the 1s
 * startup CPU limit (developers.cloudflare.com/workers/platform/limits/), and
 * constructing zod schemas at module load dominates the execution-engine
 * Worker's startup (roughly 60-90us per schema node on Cloudflare, measured
 * 2026-09-25). Contract modules declare hundreds of schemas that one request
 * touches a handful of.
 *
 * The returned value is a Proxy standing in for the built value. The first
 * forwarded touch builds it exactly once; the same stand-in is returned
 * forever, so identity comparisons between consumers stay stable.
 *
 * Forwarded (behaves as the eager schema): parsing and validation
 * (`parse`, `safeParse`, `~standard`, ...), property reads (`.shape`,
 * `.options`, `_zod`), composition (embedding in `z.object`/`z.union`/
 * `z.array`, `.extend`, `.optional()`, `.nullable()`, `.transform`, ...),
 * `instanceof`, `in`, `Object.keys` and spreading.
 *
 * NOT supported, because the stand-in and the built schema are different
 * objects and zod keys some features by object identity:
 * - JSON Schema generation that reaches a stand-in: `z.toJSONSchema(lazy)`,
 *   and any generation over a schema that embeds a stand-in (including the
 *   method forms below on such a schema). Zod's generator keys its
 *   bookkeeping by the stand-in while the built schema's processors report
 *   the real object, so output would crash or silently drop constraints. It
 *   fails loudly instead: see `LAZY_SCHEMA_JSON_SCHEMA_UNSUPPORTED`.
 *   The method forms on a lazy ROOT, `lazy.toJSONSchema()` and
 *   `lazy["~standard"].jsonSchema`, do work: they forward to the built
 *   schema and generate from it, as long as it embeds no other stand-in.
 * - Registries and metadata: `z.globalRegistry.get(lazy)` misses, and
 *   `.meta()`/`.describe()` return a new eager schema registered under the
 *   built object, not the stand-in.
 * - `structuredClone` and `Object.freeze`/`Object.seal`/`preventExtensions`
 *   throw (a Proxy cannot be cloned; the stand-in always reports extensible).
 * - `Array.isArray` is false: wrap schemas and plain-object shapes, never
 *   arrays.
 *
 * Use `isLazySchema` to detect a stand-in. Use it only for values whose
 * construction is pure (schemas and plain-object schema shapes). A builder
 * that throws rethrows on every touch until it succeeds, as the module load
 * would have failed.
 */
const lazySchemas = new WeakSet<object>();

/** True when `value` is a stand-in returned by `lazySchema` (never builds it). */
export const isLazySchema = (value: unknown): boolean =>
  typeof value === "object" && value !== null && lazySchemas.has(value);

export const LAZY_SCHEMA_JSON_SCHEMA_UNSUPPORTED =
  "lazy_schema_json_schema_unsupported: JSON Schema generation cannot see through lazySchema stand-ins (@repo/lazy-schema); build an eager schema for JSON Schema output";

const throwJsonSchemaUnsupported = (): never => {
  throw new Error(LAZY_SCHEMA_JSON_SCHEMA_UNSUPPORTED);
};

// `@__NO_SIDE_EFFECTS__` lets a bundler drop a lazy schema nothing references
// (registering the stand-in is then unobservable). Rolldown/Rollup (Vite,
// webapp-v2) honor it across modules; esbuild 0.28 (wrangler) honors it only
// for calls in this file, so Worker bundles still keep unused lazy schemas,
// which is cheap because they are never built.
export const lazySchema = /* @__NO_SIDE_EFFECTS__ */ <T extends object>(
  build: () => T,
): T => {
  let built: T | undefined;
  const target = (): T => (built ??= build());
  // Zod's JSON Schema generator calls `schema._zod.toJSONSchema?.()` as an
  // override hook before anything else, and nothing else reads that key.
  // The stand-in's `_zod` inherits every internal from the built schema's
  // `_zod` (so parsing and composition are unaffected) but overrides the hook
  // to throw, so generation that reaches a stand-in fails loudly instead of
  // producing wrong output. Built once, on the first `_zod` read.
  let internals: object | undefined;
  const readInternals = (): unknown => {
    if (internals) return internals;
    const real: unknown = Reflect.get(target(), "_zod");
    // Plain-object shapes have no `_zod`; forward whatever is there.
    if (typeof real !== "object" || real === null) return real;
    const derived: object = Object.create(real, {
      toJSONSchema: { value: throwJsonSchemaUnsupported },
    });
    internals = derived;
    return derived;
  };
  const handler: ProxyHandler<object> = {
    get: (_stub, key) =>
      key === "_zod" ? readInternals() : Reflect.get(target(), key),
    set: (_stub, key, value) => Reflect.set(target(), key, value),
    has: (_stub, key) => Reflect.has(target(), key),
    ownKeys: () => Reflect.ownKeys(target()),
    getOwnPropertyDescriptor: (_stub, key) => {
      const descriptor = Reflect.getOwnPropertyDescriptor(target(), key);
      // The stub owns no properties, so a proxy may not report one as
      // non-configurable; report it configurable (values are unchanged).
      return descriptor ? { ...descriptor, configurable: true } : undefined;
    },
    defineProperty: (_stub, key, descriptor) =>
      Reflect.defineProperty(target(), key, descriptor),
    deleteProperty: (_stub, key) => Reflect.deleteProperty(target(), key),
    getPrototypeOf: () => Reflect.getPrototypeOf(target()),
    isExtensible: () => true,
  };
  // The stand-in's own type is irrelevant: every supported operation is
  // forwarded to the built value of type T.
  const standIn = new Proxy({}, handler) as T;
  lazySchemas.add(standIn);
  return standIn;
};
